import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aboutInfo } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbs = [
    { label: '首页', href: '/' },
    { label: '关于我们', href: '/about' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete('phone_alt');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9mtunobdkpk99j7ge2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = (parsed && typeof parsed === 'object')
        ? (parsed as Record<string, unknown>).meta && typeof (parsed as Record<string, unknown>).meta === 'object'
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
          : (parsed as Record<string, string>).message || ''
        : '';

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后再试。');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="webpage"
        pageTitle="关于我们 - 蘑菇视频"
        pageDescription="蘑菇视频品牌介绍、用户口碑和联系方式。蘑菇视频致力于为用户提供优质影视内容，涵盖韩国电影、美国大片、热播剧集、动漫番剧，高清免费在线观看。"
        pageUrl="https://mogu.video/about"
        breadcrumbs={breadcrumbs}
        faqData={[
          { question: '蘑菇视频是免费的吗？', answer: '是的，蘑菇视频所有内容完全免费观看，不需要会员也不需要付费。' },
          { question: '蘑菇视频支持哪些设备？', answer: '蘑菇视频支持电脑、手机、平板等多种设备，随时随地在线观看。' },
          { question: '视频画质如何？', answer: '蘑菇视频提供1080P蓝光高清画质，采用先进的CDN加速技术，播放流畅不卡顿。' },
          { question: '如何联系我们？', answer: '您可以通过邮箱contact@mogu.video或电话400-888-8888与我们取得联系。' },
        ]}
      />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        <div className="pt-20 pb-4 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              {breadcrumbs.map((crumb, i) => (
                <span key={crumb.href} className="flex items-center gap-2">
                  {i > 0 && (
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </span>
                  )}
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-foreground-800 font-medium">{crumb.label}</span>
                  ) : (
                    <a
                      href={crumb.href}
                      onClick={(e) => { e.preventDefault(); navigate(crumb.href); }}
                      className="hover:text-primary-500 cursor-pointer transition-colors"
                    >
                      {crumb.label}
                    </a>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        <section className="px-4 md:px-6 pb-10">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于蘑菇视频
            </h1>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.brandIntro}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-4">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-2">
              我们的使命
            </h2>
            <p className="text-base text-foreground-600 text-center max-w-2xl mx-auto mb-10">
              {aboutInfo.mission}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.features.map((feature) => (
                <div key={feature.title} className="p-5 bg-background-100 rounded-lg text-center">
                  <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className={`${feature.icon} text-xl`} style={{ color: 'oklch(var(--primary-500))' }}></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              来自真实用户的评价
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="p-5 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-3 h-3 flex items-center justify-center">
                        <i
                          className={`ri-star-fill text-xs ${
                            i < t.rating ? 'text-accent-500' : 'text-foreground-300'
                          }`}
                        ></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{t.content}&rdquo;</p>
                  <p className="text-xs font-medium text-foreground-900">—— {t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议，欢迎随时与我们联系
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-lg" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-wechat-line text-lg" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">微信</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-phone-line text-lg" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">电话</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-background-100 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-map-pin-line text-lg" style={{ color: 'oklch(var(--primary-500))' }}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">地址</p>
                  <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                </div>
              </div>
            </div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
              </div>

              <div className="honeypot-wrap">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg cursor-pointer transition-colors whitespace-nowrap"
              >
                提交留言
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-center" style={{ color: 'oklch(var(--accent-600))' }}>
                  感谢您的留言，我们会尽快回复！
                </p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-center text-primary-600">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}